/**
 * Places a student can be sent to that are not tools.
 *
 * The finder lists every tool in aisle order, and until now nothing else - so
 * the sub-fab, which has a stair and a room and a live utility board, could be
 * walked to but not found. A place is the finder's answer: it sits after the
 * last station, searches by the same handles, and jumps the same way.
 *
 * A place carries its own viewpoint in the shape `FabScene.hmiViewpoint`
 * returns for a tool - an eye and a point to look at - so `jumpToTool` in
 * main.tsx treats the two alike and the glide, pacing, and reduced-motion
 * handling are shared rather than duplicated. It is NOT a Tool: nothing here
 * reaches the floor builder, the panel registry, or the harnesses that assert
 * every station declares its knobs.
 */

import type { Vector3Tuple } from 'three'
import { t } from '../i18n/i18n'
import { AIRLOCK, SUBFAB } from './subfabLayout'
import { EYE_HEIGHT } from './walkPhysics'

export interface Place {
  /** Stable id, also the i18n key under `place.*`. */
  id: string
  /** Stencilled label, shown where a tool would show its asset tag. */
  tag: string
  /** Where the student lands, metres. */
  eye: Vector3Tuple
  /** What they are looking at on arrival. */
  target: Vector3Tuple
}

const stair = SUBFAB.stair
const stairZ = (stair.minZ + stair.maxZ) / 2

export const PLACES: Place[] = [
  {
    id: 'subfab',
    tag: 'SUB-FAB',
    // ON the sub-fab level: past the foot of the stair and clear of the
    // lower airlock door, at eye height above the plant slab, looking into
    // the room. Earlier landings put the student at the stair head on the
    // fab floor looking down the flight, which read as "go to the stair"
    // rather than "go to the sub-fab" - the descent, and the airlock, were
    // still ahead of them. The glide from the floor passes through the slab
    // for a moment; the arrival is the point. The eye sits 1.6 m past the
    // lower door, and a step off the wall, so the door's own collider and
    // approach are behind it and the shaft enclosure is beside rather than
    // in front.
    eye: [AIRLOCK.lowerDoorX + 2.5, EYE_HEIGHT - SUBFAB.depth, stairZ - 1.2],
    // Looking down the room's long axis toward its far corner rather than
    // at its centre: the centre line ran straight into the skid beside the
    // stair foot, and a wall filled half the arrival frame.
    target: [stair.maxX + 6, EYE_HEIGHT - SUBFAB.depth, SUBFAB.minZ + 4],
  },
]

export const placeName = (p: Place): string => t(`place.${p.id}.Name`)
export const placeWhere = (p: Place): string => t(`place.${p.id}.Where`)
